
import React from "react";
import ServiceProfile from "@/components/ServiceProfile";
import ReviewList from "@/components/ReviewList";
import { Provider, ReviewUI } from "@/types/supabase";
import AdUnit from "@/components/ads/AdUnit";
import AfterIdle from "@/components/util/AfterIdle";
import { debugLogger } from '@/utils/debug';
// No longer needed here: import ServiceSidebar from "@/components/service/ServiceSidebar"; // This import was incorrect anyway

interface ServiceDetailsProps {
  provider: Provider;
  reviews: ReviewUI[];
  averageRating: number;
  totalReviews: number;
  isLoadingReviews: boolean;
  onReviewUpdated: () => void;
  hasUserReviewed: boolean; // Already in props, was missing in the error message context
  serviceId: string;      // Already in props, was missing in the error message context
  isPremiumProvider: boolean; // New prop
  subscriptionTier: string; // New prop
  adsEnabled: boolean; // New prop
}

const ServiceDetails = ({ 
  provider, 
  reviews, 
  averageRating, 
  totalReviews, 
  isLoadingReviews,
  onReviewUpdated,
  hasUserReviewed, // Prop used by parent, not directly here
  serviceId,       // Prop used by parent, not directly here
  isPremiumProvider, // Use this prop
  subscriptionTier,
  adsEnabled
}: ServiceDetailsProps) => {
  // Internal state and effects for isPremiumProvider and isSimulatedPremiumListing are removed.
  // These values will now come from props.
  
  if (!provider) {
    console.error("ServiceDetails received null or undefined provider");
    return <div className="text-center p-8">Service provider not found</div>;
  }
  
  // Log provider data for debugging
  debugLogger.log("ServiceDetails - Rendering provider:", provider);
  debugLogger.log("ServiceDetails - isPremiumProvider (prop):", isPremiumProvider);
  debugLogger.log("ServiceDetails - subscriptionTier (prop):", subscriptionTier);
  debugLogger.log("ServiceDetails - adsEnabled (prop):", adsEnabled);
  
  return (
    <div className="space-y-8">
      <ServiceProfile
        id={provider.id}
        name={provider.name}
        image={provider.image_url || ""}
        categories={provider.categories || [provider.category]}
        description={provider.description || ""}
        suburbs={provider.suburbs || []}
        rating={averageRating}
        reviewCount={totalReviews}
        user_id={provider.user_id}
        isVerified={provider.is_verified || false}
        isFeatured={provider.is_featured || false}
        isTopRated={provider.is_top_rated || averageRating >= 4.5}
        joinedDate={new Date(provider.created_at).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long'
        })}
  websiteUrl={provider.website_url ?? undefined}
        subscriptionTier={subscriptionTier}
        whatsapp_number={provider.whatsapp_number}
      />

      {/* Banner ad below service description - only show for non-premium providers */}
      {adsEnabled && !isPremiumProvider && (
        <AfterIdle timeoutMs={1200}>
          <div className="w-full rounded-md border border-brand-cloud bg-white p-4">
            <div className="flex justify-center">
              <AdUnit adSlot="7965312393" adFormat="BANNER" className="w-full" />
            </div>
          </div>
        </AfterIdle>
      )}

      <div id="reviewsSection">
        <ReviewList 
          reviews={reviews} 
          isLoading={isLoadingReviews}
          isPremiumProvider={isPremiumProvider}
          adsEnabled={adsEnabled}
        />
      </div>

      {/* Responsive ad unit below reviews section - only show for non-premium providers */}
      {adsEnabled && !isPremiumProvider && (
        <AfterIdle timeoutMs={1200}>
          <div className="mt-6 w-full rounded-md border border-brand-cloud bg-white p-4">
            <div className="flex justify-center">
              <AdUnit adSlot="1714990002" adFormat="LEADERBOARD" className="w-full" />
            </div>
          </div>
        </AfterIdle>
      )}
    </div>
  );
};

export default ServiceDetails;
